<template>
    <div :class="className">
        <div class="inputwrapper">
            <v-textarea
                v-if="type == 'textarea'"
                v-model="inputText"
                :clearable="clearable"
                :required="required"
                :type="type"
                :label="label"
                :disabled="disabled"
                :placeholder="placeholder"
                :format="format"
                :autofocus="autofocus"
                :error-messages="errorMessages"
                :color="color"
                ref="input"
                hide-details
                :dense="dense"
                autocomplete="off"
                :loading="loading"
                @keydown.up="upInterceptor"
                @keydown.down="downInterceptor"
                @keydown.enter="enterInterceptor"
                @keydown="$emit('keydown', inputVal)"
                @focusout="focusOut"
                :max-width="maxWidth"
                :min-width="minWidth"
            >
                <template v-if="$scopedSlots.label" #label>
                    <slot name="label" />
                </template>
            </v-textarea>
            <input
                v-else-if="type == 'radio'"
                :id="name"
                :required="required"
                :type="type"
                :disabled="disabled"
                :color="color"
                :placeholder="placeholder"
                :autofocus="autofocus"
                :checked="inputVal == 'on'"
                :loading="loading"
                ref="input"
                @keydown.up="upInterceptor"
                @keydown.down="downInterceptor"
                @keydown.enter="enterInterceptor"
                @focusout="focusOut"
            />

            <v-text-field
                v-else
                :single-line="singleLine"
                :outlined="outlined"
                v-model="inputText"
                :clearable="clearable"
                :type="inputType"
                :label="label + (required ? ' (*)' : '')"
                :disabled="disabled"
                :placeholder="placeholder"
                :autofocus="autofocus"
                :color="color"
                :step="step"
                :max="max"
                :min="min"
                :dense="dense"
                :error-messages="errorMessages"
                :loading="loading"
                :hide-details="resolvedHideDetails"
                :hint="hint"
                :persistent-hint="Boolean(hint)"
                :prefix="prefix"
                inputmode="decimal"
                autocomplete="off"
                ref="input"
                :persistent-placeholder="persistentPlaceholder"
                @keydown.up="upInterceptor"
                @keydown.down="downInterceptor"
                @keydown.enter="enterInterceptor"
                @keydown="keydown"
                @focusout="focusOut"
                :max-width="maxWidth"
                :min-width="minWidth"
            >
                <template v-if="$scopedSlots.label" #label>
                    <slot name="label" />
                </template>
            </v-text-field>
            <span class="ml-2" v-if="label && type == 'checkbox'">
                {{ label }}
            </span>
        </div>
        <div v-if="errorMessages" class="alert alert-danger mt-2" role="alert">
            <div v-for="(error, index) in errorMessages" :key="index">
                {{ error }}
            </div>
        </div>
    </div>
</template>

<script>
import {
    parseDecimalInput as parseAmountInput,
    sanitizeDecimalInputString as sanitizeAmountInputString,
} from "@/utils/numberFormat";

export default {
    name: "Field",
    props: {
        value: {
            type: [String, Number, Boolean],
            required: false,
            default: null,
        },
        className: {
            type: String,
            default: "mb-3",
        },
        persistentPlaceholder: {
            type: Boolean,
            default: false,
        },
        singleLine: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: "",
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        type: {
            type: String,
            required: false,
            default: "text",
        },
        min: {
            type: Number,
            required: false,
        },
        max: {
            type: Number,
            required: false,
        },
        errorMessages: {
            type: Array,
            required: false,
            default: null,
        },
        label: {
            type: String,
            required: false,
        },
        name: {
            type: String,
            required: false,
            default: null,
        },
        hint: {
            type: String,
            required: false,
            default: null,
        },
        required: {
            type: Boolean,
            required: false,
        },
        disabled: {
            type: Boolean,
            required: false,
        },
        format: {
            type: String,
            required: false,
        },
        autofocus: {
            type: Boolean,
            default: false,
        },
        step: {
            type: String,
            required: false,
        },
        color: {
            type: String,
            required: false,
        },
        outlined: { type: Boolean, default: false },
        dense: {
            type: Boolean,
            default: false,
        },
        prefix: {
            type: String,
            required: false,
            default: null,
        },
        hideDetails: {
            type: [Boolean, String],
            required: false,
            default: false,
        },
        minWidth: {
            type: Number,
            required: false,
        },
        maxWidth: {
            type: Number,
            required: false,
        },
    },
    data() {
        return {
            inputVal: null,
        };
    },
    computed: {
        tagname() {
            if (this.type == "textarea") {
                return "textarea";
            }
            return "input";
        },
        inputText: {
            get() {
                if (this.inputVal === null || this.inputVal === undefined) {
                    return "";
                }
                return this.inputVal;
            },
            set(newValue) {
                if (newValue === "" || newValue === null) {
                    this.inputVal = null;
                    return;
                }
                if (this.format === "number") {
                    this.inputVal = sanitizeAmountInputString(newValue);
                    return;
                }
                switch (this.type) {
                    case "integer":
                        this.inputVal = parseInt(newValue, 10);
                        break;
                    case "float":
                        this.inputVal = parseFloat(newValue);
                        break;
                    default:
                        this.inputVal = newValue;
                }
            },
        },
        resolvedHideDetails() {
            if (this.hideDetails === "auto") {
                return "auto";
            }
            return Boolean(this.hideDetails);
        },
        inputType() {
            // Decimal-formatted fields use a text input so the decimal
            // separator (comma or point) can be typed regardless of locale.
            if (this.format === "number") {
                return "text";
            }
            switch (this.type) {
                case "integer":
                case "float":
                    return "number";
                default:
                    return this.type;
            }
        },
    },
    watch: {
        inputVal(inputVal) {
            const emittedValue = this.formatAmountValue(inputVal);
            this.$emit("input", emittedValue === "" ? null : emittedValue);
        },
        value(value) {
            this.syncInputValFromValue(value);
        },
    },
    created() {
        this.syncInputValFromValue(this.value);
    },
    mounted() {
        if (this.autofocus) {
            this.$refs.input.focus();
        }
    },
    methods: {
        syncInputValFromValue(value) {
            if (value === null || value === undefined || value === "") {
                this.inputVal = null;
                return;
            }
            if (this.format === "number") {
                this.inputVal = sanitizeAmountInputString(value);
                return;
            }
            this.inputVal = value;
        },
        formatAmountValue(value) {
            if (this.format !== "number") {
                return value;
            }
            if (value === null || value === undefined || value === "") {
                return null;
            }
            const normalized = sanitizeAmountInputString(value);
            if (normalized.endsWith(".")) {
                return normalized;
            }
            return parseAmountInput(normalized);
        },
        keydown(inputVal) {
            this.$emit("keydown", inputVal);
        },
        focus() {
            this.$refs.input.focus();
        },
        focusOut() {
            if (this.format === "number") {
                this.inputVal = parseAmountInput(this.inputVal);
            }
            const emittedValue = this.formatAmountValue(this.inputVal);
            this.$emit("input", emittedValue === "" ? null : emittedValue);
            this.$emit("focusout", emittedValue === "" ? null : emittedValue);
        },
        upInterceptor() {
            this.$emit("navigationUp");
        },
        downInterceptor() {
            this.$emit("navigationDown");
        },
        enterInterceptor() {
            this.$emit("navigationEnter");
        },
    },
};
</script>
